<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/topics/${topicId}/lessons`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit Lesson</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-8 mb-3">
                  <label class="form-label">Lesson Title <span class="text-danger">*</span></label>
                  <input v-model="form.title" type="text" class="form-control" required />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Duration (minutes)</label>
                  <input v-model.number="form.duration" type="number" class="form-control" min="1" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Content</label>
                <textarea v-model="form.content" class="form-control" rows="6"></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label">Video URL (optional)</label>
                <input v-model="form.videoUrl" type="url" class="form-control" />
              </div>

              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Display Order</label>
                  <input v-model.number="form.order" type="number" class="form-control" min="1" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 flex-wrap pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update Lesson
                </button>
                <router-link :to="`/app/lessons/${lessonId}/tests`" class="btn btn-outline-info">
                  <vue-feather type="check-square" size="14" class="me-1" />
                  Manage Tests
                </router-link>
                <router-link :to="`/app/lessons/${lessonId}/notes`" class="btn btn-outline-warning">
                  <vue-feather type="file-text" size="14" class="me-1" />
                  Manage Notes
                </router-link>
                <router-link :to="`/app/topics/${topicId}/lessons`" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditLesson',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const lessonId = route.params.id

    const lesson = ref(null)
    const topicId = ref(null)
    const form = ref({
      title: '',
      content: '',
      videoUrl: '',
      duration: null,
      order: 1,
      status: 'draft'
    })
    const loading = ref(false)
    const error = ref('')

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      // Exam → Subject → Topic → Lesson (edit)
      if (lesson.value?.examId) {
        items.push({ title: lesson.value?.examName || 'Exam', url: `/app/exams/${lesson.value.examId}/subjects` })
      }
      if (lesson.value?.subjectId) {
        items.push({ title: lesson.value?.subjectName || 'Subject', url: `/app/subjects/${lesson.value.subjectId}/topics` })
      }
      if (lesson.value?.topicId) {
        items.push({ title: lesson.value?.topicName || 'Topic', url: `/app/topics/${lesson.value.topicId}/lessons` })
      }
      items.push({ title: lesson.value?.title || 'Edit Lesson', active: true })
      return items
    })

    const fetchLesson = async () => {
      try {
        const response = await api.get(`/v1/admin/lessons/${lessonId}`)
        lesson.value = response.data.lesson
        topicId.value = response.data.lesson.topicId
        form.value = {
          title: response.data.lesson.title,
          content: response.data.lesson.content || '',
          videoUrl: response.data.lesson.videoUrl || '',
          duration: response.data.lesson.duration,
          order: response.data.lesson.order || 1,
          status: response.data.lesson.status || 'draft'
        }
      } catch (err) {
        alert.error('Error', 'Lesson not found')
        router.push('/app/exams/list')
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`/v1/admin/lessons/${lessonId}`, form.value)
        alert.success('Success', 'Lesson updated successfully')
        router.push(`/app/topics/${topicId.value}/lessons`)
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update lesson'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchLesson)

    return { lessonId, topicId, form, loading, error, breadcrumbs, handleSubmit }
  }
}
</script>
